/**
 * Multi-Tab Activity Synchronization (Multi-Channel Support)
 * Syncs user activity across all open browser tabs
 * Supports role-specific channels for user, admin, and nominee
 *
 * Features:
 * - Real-time activity broadcasting
 * - Automatic tab synchronization
 * - Warning sync across tabs
 * - Logout sync across tabs
 * - Role-specific isolation
 */

const DEFAULT_CHANNEL_NAME = 'digitalvault-session';
const MESSAGE_TYPES = {
  ACTIVITY: 'ACTIVITY',
  WARNING: 'WARNING',
  LOGOUT: 'LOGOUT',
  SESSION_EXTENDED: 'SESSION_EXTENDED',
  NEW_SESSION: 'NEW_SESSION'
};

// Store multiple channels (Map: channelName -> { channel, handler })
const channels = new Map();

/**
 * Initialize multi-tab synchronization for a specific channel
 * @param {Object} callbacks - Event handlers
 * @param {Function} callbacks.onActivity - Called when activity detected in another tab
 * @param {Function} callbacks.onWarning - Called when warning shown in another tab
 * @param {Function} callbacks.onLogout - Called when logout triggered in another tab
 * @param {Function} callbacks.onSessionExtended - Called when session extended in another tab
 * @param {string} channelName - Channel name (default: digitalvault-session)
 */
export const initTabSync = (callbacks = {}, channelName = DEFAULT_CHANNEL_NAME) => {
  // Check if BroadcastChannel is supported
  if (typeof BroadcastChannel === 'undefined') {
    return false;
  }

  // Check if channel already exists
  if (channels.has(channelName)) {
    return true;
  }

  // Create broadcast channel
  const broadcastChannel = new BroadcastChannel(channelName);

  // Setup message handler
  const messageHandler = (event) => {
    const { type, data } = event.data;

    switch (type) {
      case MESSAGE_TYPES.ACTIVITY:
        if (callbacks.onActivity) {
          callbacks.onActivity(data);
        }
        break;

      case MESSAGE_TYPES.WARNING:
        if (callbacks.onWarning) {
          callbacks.onWarning(data);
        }
        break;

      case MESSAGE_TYPES.LOGOUT:
        if (callbacks.onLogout) {
          callbacks.onLogout(data);
        }
        break;

      case MESSAGE_TYPES.SESSION_EXTENDED:
        if (callbacks.onSessionExtended) {
          callbacks.onSessionExtended(data);
        }
        break;

      case MESSAGE_TYPES.NEW_SESSION:
        if (callbacks.onNewSession) {
          callbacks.onNewSession(data);
        }
        break;

      default:
        // Unknown message type
    }
  };

  // Listen for messages
  broadcastChannel.onmessage = messageHandler;

  // Store channel and handler
  channels.set(channelName, { channel: broadcastChannel, handler: messageHandler });

  return true;
};

/**
 * Broadcast user activity to other tabs
 * @param {Object} data - Activity data (optional)
 * @param {string} channelName - Channel name (default: digitalvault-session)
 */
export const broadcastActivity = (data = {}, channelName = DEFAULT_CHANNEL_NAME) => {
  const channelData = channels.get(channelName);
  if (!channelData) return;

  channelData.channel.postMessage({
    type: MESSAGE_TYPES.ACTIVITY,
    data: {
      timestamp: Date.now(),
      ...data
    }
  });
};

/**
 * Broadcast warning shown to other tabs
 * @param {number} secondsRemaining - Seconds until logout
 * @param {string} channelName - Channel name (default: digitalvault-session)
 */
export const broadcastWarning = (secondsRemaining, channelName = DEFAULT_CHANNEL_NAME) => {
  const channelData = channels.get(channelName);
  if (!channelData) return;

  channelData.channel.postMessage({
    type: MESSAGE_TYPES.WARNING,
    data: {
      secondsRemaining,
      timestamp: Date.now()
    }
  });
};

/**
 * Broadcast logout to other tabs
 * @param {string} reason - Logout reason
 * @param {string} channelName - Channel name (default: digitalvault-session)
 */
export const broadcastLogout = (reason = 'inactivity', channelName = DEFAULT_CHANNEL_NAME) => {
  const channelData = channels.get(channelName);
  if (!channelData) return;

  channelData.channel.postMessage({
    type: MESSAGE_TYPES.LOGOUT,
    data: {
      reason,
      timestamp: Date.now()
    }
  });
};

/**
 * Broadcast session extension to other tabs
 * @param {string} channelName - Channel name (default: digitalvault-session)
 */
export const broadcastSessionExtended = (channelName = DEFAULT_CHANNEL_NAME) => {
  const channelData = channels.get(channelName);
  if (!channelData) return;

  channelData.channel.postMessage({
    type: MESSAGE_TYPES.SESSION_EXTENDED,
    data: {
      timestamp: Date.now()
    }
  });
};

/**
 * Broadcast new session creation to other tabs
 * @param {string} roleType - Role that logged in (user/admin/nominee)
 * @param {string} channelName - Channel name (default: digitalvault-session)
 */
export const broadcastNewSession = (roleType, channelName = DEFAULT_CHANNEL_NAME) => {
  const channelData = channels.get(channelName);
  if (!channelData) return;

  channelData.channel.postMessage({
    type: MESSAGE_TYPES.NEW_SESSION,
    data: {
      roleType,
      timestamp: Date.now()
    }
  });
};

/**
 * Stop multi-tab synchronization for a specific channel
 * @param {string} channelName - Channel name (default: digitalvault-session)
 */
export const stopTabSync = (channelName = DEFAULT_CHANNEL_NAME) => {
  const channelData = channels.get(channelName);
  if (channelData) {
    channelData.channel.close();
    channels.delete(channelName);
  }
};

/**
 * Stop all channels
 */
export const stopAllChannels = () => {
  channels.forEach((data, name) => {
    data.channel.close();
  });
  channels.clear();
};

/**
 * Check if specific channel is active
 * @param {string} channelName - Channel name (default: digitalvault-session)
 */
export const isChannelActive = (channelName = DEFAULT_CHANNEL_NAME) => {
  return channels.has(channelName);
};

/**
 * Check if tab sync is active (backwards compatibility)
 * Checks if default channel is active
 */
export const isTabSyncActive = () => {
  return channels.has(DEFAULT_CHANNEL_NAME);
};

export default {
  initTabSync,
  broadcastActivity,
  broadcastWarning,
  broadcastLogout,
  broadcastSessionExtended,
  broadcastNewSession,
  stopTabSync,
  stopAllChannels,
  isChannelActive,
  isTabSyncActive
};
